import React, {useState} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';

import type {Asset} from '@deviceinsight/ng-ui-api-client';
import {
	DATAPOINT_IS_ONLINE,
	DATAPOINT_PIN1_FREQUENCY,
	DATAPOINT_PIN1_MODE,
	DATAPOINT_PIN1_STATUS,
	DATAPOINT_PIN2_FREQUENCY,
	DATAPOINT_PIN2_MODE,
	DATAPOINT_PIN2_STATUS,
	DATAPOINT_PIN3_FREQUENCY,
	DATAPOINT_PIN3_MODE,
	DATAPOINT_PIN3_STATUS,
	DATAPOINT_PIN4_FREQUENCY,
	DATAPOINT_PIN4_MODE,
	DATAPOINT_PIN4_STATUS,
	GPIO_MODE_INPUT,
	GPIO_MODE_OUTPUT
} from '../../../modules/common/consts';
import './DigitalTwinWidget.less';
import {getDigitalTwinDatapointsByAsset, useAssetSubscription} from '../common/useAssetSubscription';
import useDeviceSvg from './useDeviceSvg';
import useDeviceColors from './useDeviceColors';
import BlinkTrigger from './BlinkTrigger';
import {mapLiveDataOnlineBased} from '../../common/onlineBasedLiveDataMapper';
import type {ButtonInfo} from '../floorplan/types';
import useButtonTriggers from '../../common/hooks/useButtonTriggers';
import useButtonInteractivity from './useButtonInteractivity';
import UserStore from '../../../modules/common/UserStore';
import {WidgetSvg} from '../floorplan/hooks/types';
import {WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';

const getAssetFromContext = (context: WidgetProps['context']): Asset => {
	if (context.type !== 'asset') {
		throw new Error('Error getting Asset from Context');
	}

	return context.asset;
};

type StateProps = {
	dataPointValue: boolean | null | undefined;
};
type ModeProps = {
	mode: string | null | undefined;
};
type GpioPinProps = {
	state: boolean | null | undefined;
	mode: string | null | undefined;
	frequency: number | null | undefined;
};

const GpioMode = ({mode}: ModeProps) => {
	switch (mode) {
		case GPIO_MODE_INPUT:
			return <Trans i18nKey="ruleengine.valueTypes.input">Input</Trans>;

		case GPIO_MODE_OUTPUT:
			return <Trans i18nKey="ruleengine.valueTypes.output">Output</Trans>;

		default:
			return <Trans i18nKey="ruleengine.valueTypes.unknown">Unknown</Trans>;
	}
};

const GpioState = ({dataPointValue}: StateProps) => {
	switch (dataPointValue) {
		case true:
			return <Trans i18nKey="ruleengine.valueTypes.high">High</Trans>;

		case false:
			return <Trans i18nKey="ruleengine.valueTypes.low">Low</Trans>;

		default:
			return <Trans i18nKey="ruleengine.valueTypes.unknown">Unknown</Trans>;
	}
};

const GpioPin = ({state, mode, frequency}: GpioPinProps) => {
	return mode ? (
		<span>
			<GpioMode mode={mode} /> | <GpioState dataPointValue={state} /> | {frequency} mHz
		</span>
	) : (
		<Trans i18nKey="ruleengine.valueTypes.unknown">Unknown</Trans>
	);
};

const OnlineState = ({dataPointValue}: StateProps) => {
	switch (dataPointValue) {
		case true:
			return <Trans i18nKey="rafi.ruleengine.status.online">Online</Trans>;

		case false:
			return <Trans i18nKey="rafi.ruleengine.status.offline">Offline</Trans>;

		default:
			return <Trans i18nKey="ruleengine.status.unknown">Unknown</Trans>;
	}
};

const DigitalTwinWidget = (props: WidgetProps) => {
	const {
		context,
		config: {key}
	} = props;
	const user = UserStore.getUser();
	const canPressButton = user && user.authorities.includes('WRITE_BUTTON_EVENT');
	const canSetColor = user && user.authorities.includes('WRITE_LED_ACTION');
	const [svgRootObj, setSvgRootObj] = useState<WidgetSvg>();
	const [asset] = useState(getAssetFromContext(context));
	const [buttonInfo, setButtonInfo] = useState<ButtonInfo>();
	const liveData = useAssetSubscription(asset, getDigitalTwinDatapointsByAsset, mapLiveDataOnlineBased);
	const isOnline = liveData[DATAPOINT_IS_ONLINE];
	useDeviceSvg(asset, key, setSvgRootObj);
	useButtonInteractivity(svgRootObj, canPressButton, setButtonInfo, asset);
	useDeviceColors(asset, svgRootObj, liveData, key);
	useButtonTriggers(asset, isOnline, buttonInfo, setButtonInfo);
	const containerId = `svg-digitaltwin-${key}`;
	return (
		<div className="dash-box digital-twin-widget">
			<div
				id={containerId}
				style={{
					width: '100%',
					height: '100%'
				}}
				className="digitaltwin-widget-container"
			/>
			<div className="digitaltwin-widget-info">
				<h2>
					<Trans i18nKey="dashboard.widgets.digitaltwin.assetinfo">Asset Info</Trans>
				</h2>
				<div className="info-row">
					<label>
						<Trans i18nKey="rafi.ruleengine.gpio1">GPIO 1</Trans>
					</label>
					<GpioPin
						state={liveData[DATAPOINT_PIN1_STATUS]}
						mode={liveData[DATAPOINT_PIN1_MODE]}
						frequency={liveData[DATAPOINT_PIN1_FREQUENCY]}
					/>
				</div>
				<div className="info-row">
					<label>
						<Trans i18nKey="rafi.ruleengine.gpio2">GPIO 2</Trans>
					</label>
					<GpioPin
						state={liveData[DATAPOINT_PIN2_STATUS]}
						mode={liveData[DATAPOINT_PIN2_MODE]}
						frequency={liveData[DATAPOINT_PIN2_FREQUENCY]}
					/>
				</div>
				<div className="info-row">
					<label>
						<Trans i18nKey="rafi.ruleengine.gpio3">GPIO 3</Trans>
					</label>
					<GpioPin
						state={liveData[DATAPOINT_PIN3_STATUS]}
						mode={liveData[DATAPOINT_PIN3_MODE]}
						frequency={liveData[DATAPOINT_PIN3_FREQUENCY]}
					/>
				</div>
				<div className="info-row">
					<label>
						<Trans i18nKey="rafi.ruleengine.gpio4">GPIO 4</Trans>
					</label>
					<GpioPin
						state={liveData[DATAPOINT_PIN4_STATUS]}
						mode={liveData[DATAPOINT_PIN4_MODE]}
						frequency={liveData[DATAPOINT_PIN4_FREQUENCY]}
					/>
				</div>
				<div className="info-row">
					<label>
						<Trans i18nKey="rafi.ruleengine.status.label">Status</Trans>
					</label>
					<OnlineState dataPointValue={liveData[DATAPOINT_IS_ONLINE]} />
				</div>
				{isOnline && <BlinkTrigger asset={asset} assetLiveData={liveData} canSetColor={canSetColor} />}
			</div>
		</div>
	);
};

export default DigitalTwinWidget;
